import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthenticationClient } from '../../core/service/authentication-client.service';
import { NavigationService } from '../../core/service/navigation.service';
import { SessionService } from '../../core/service/session.service';
import { SidebarService } from '../../core/service/sidebar.service';
import { AccountDefinition } from '../model/account-definition';
import { AutoLogoutService } from './auto-logout-service';
import { AccountService } from './data/account.service';
import { ProfileService } from './data/profile.service';
import { Features, ModuleNames } from '../model/account-definition';
import { LicenseService } from './data/license.service';
import { BankIdService } from './data/bankId.service';
import { environment } from '../../../environments/environment';
import { BasicOwnerConfig } from '../model/basic-owner-config';

function setHotjarAttributes(acc: AccountDefinition | null): void {
  try {
    if (acc?.id?.length) {
      hj('identify', acc.id, {
        production: environment.production ?? false,
        modules: Array.isArray(acc.modules) ? acc.modules.join(',') : '',
      });
      console.log('Hotjar attributes updated');
    } else {
      hj('identify', null, {});
      console.log('Hotjar attributes cleared');
    }
  } catch {
    console.warn('Failed to update Hotjar attributes');
  }
}

@Injectable({ providedIn: 'root' })
export class UserAccountService {
  profileSettings: BasicOwnerConfig;
  constructor(
    private readonly _client: AuthenticationClient,
    private readonly _dataService: AccountService,
    private readonly _licenseService: LicenseService,
    private readonly _navigation: NavigationService,
    private readonly _session: SessionService,
    private readonly _autoLogoutService: AutoLogoutService,
    private readonly _profileService: ProfileService,
    private readonly _sidebarService: SidebarService,
    private readonly _bankIdService: BankIdService
  ) {}

  private _account: Observable<AccountDefinition>;
  private _accountLogout = new Subject<void>();
  private _refreshProfileSettings = new BehaviorSubject<null>(null);
  get current(): Observable<AccountDefinition> {
    if (!this._account) {
      this._account = combineLatest([
        this._refreshProfileSettings.pipe(switchMap(() => this._profileService.getConfig())),
        this._dataService.getAccountDefinition(),
      ]).pipe(
        takeUntil(this._accountLogout),
        map(([ps, ad]) => {
          this.profileSettings = ps;
          setHotjarAttributes(ad);
          return new AccountDefinition(ad, this.profileSettings);
        }),
        catchError(err => {
          this.logout();
          throw err;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this._account;
  }

  private _isAdmin$?: Observable<boolean>;
  get isAdmin$(): Observable<boolean> {
    if (!this._isAdmin$) {
      this._isAdmin$ = this.current.pipe(
        map(account => !!account?.hasAccess(ModuleNames.System, Features.Admin)),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this._isAdmin$;
  }

  refreshProfileSettings(): void {
    this._refreshProfileSettings.next(null);
  }

  bankIdAuthStart(isMobile: boolean, thisUnit: boolean) {
    return this._bankIdService.bankIdAuthStart(isMobile, thisUnit);
  }

  bankIdAuthStatus(isMobile: boolean, thisUnit: boolean, orderReference: string) {
    return this._bankIdService.bankIdAuthStatus(isMobile, thisUnit, orderReference);
  }

  bankIdCancel(orderReference: string) {
    return this._bankIdService.bankIdCancel(orderReference);
  }

  login(userName: string, password: string, saveSession: boolean): Observable<any> {
    return this._client.authenticate(userName, password).pipe(
      tap(res => this._session.start(res.access_token, res.expires_in, saveSession)),
      switchMap(() => this.current),
      tap(x => {
        let url = this._session.redirectUrl ? this._session.redirectUrl : x.homeUrl;
        this._session.redirectUrl = null;
        this._session.setHomeUrl(x.homeUrl);
        this._autoLogoutService.timeoutInMinutes = x.timeoutInMinutes;
        this._autoLogoutService.onTimeout = () => this.logout();
        this._autoLogoutService.start();
        this._navigation.navigate([url], { queryParamsHandling: 'preserve' });
      })
    );
  }

  loginBankId(orderReference: string): Observable<any> {
    return this._client.authenticateBankId(orderReference).pipe(
      tap(res => this._session.start(res.access_token, res.expires_in, false)),
      switchMap(() => this.current),
      tap(x => {
        let url = this._session.redirectUrl ? this._session.redirectUrl : x.homeUrl;
        this._session.redirectUrl = null;
        this._session.setHomeUrl(x.homeUrl);
        this._autoLogoutService.timeoutInMinutes = x.timeoutInMinutes;
        this._autoLogoutService.onTimeout = () => this.logout();
        this._autoLogoutService.start();
        this._navigation.navigate([url], { queryParamsHandling: 'preserve' });
      })
    );
  }

  logout(): void {
    this._session.stop();
    this._accountLogout.next();
    this._account = null;
    this._isAdmin$ = undefined;
    this._licenseService?.clearLimitsCache?.();
    this._autoLogoutService.stop();
    this._navigation.navigateLogin();
    this._sidebarService.toggleSidebar(false);
    setHotjarAttributes(null);
  }
}
